"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { useAuth } from "@clerk/nextjs";
import { trackEvent } from "@/lib/utils/analytics";
import {
  Scale,
  Sliders,
  Save,
  Search,
  Check,
  Calculator,
  Quote,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/shared/ui/button";

export default function BrandStoryPage() {
  const { userId } = useAuth();
  const fadeUpRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    const currentRefs = fadeUpRefs.current;
    currentRefs.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      currentRefs.forEach((el) => {
        if (el) observer.unobserve(el);
      });
    };
  }, []);

  return (
    <div className="brand-story-page bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 antialiased font-sans selection:bg-zinc-900 dark:selection:bg-zinc-100 selection:text-white dark:selection:text-zinc-900">
      {/* 1. Hero: Essence & Tagline */}
      <section className="relative h-screen flex flex-col justify-center items-center text-center px-4 sm:px-6 bg-white dark:bg-zinc-950 overflow-hidden">
        {/* Background Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #000 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        ></div>

        <div
          ref={(el) => {
            fadeUpRefs.current[0] = el;
          }}
          className="fade-up max-w-5xl z-10"
        >
          <p className="text-zinc-400 dark:text-zinc-500 text-[10px] sm:text-xs font-bold tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-6 sm:mb-8">
            Our Mission
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.9] tracking-tighter mb-6 sm:mb-8 md:mb-10 text-zinc-900 dark:text-zinc-100 px-2 sm:px-0">
            LESS
            <br />
            COMPLEXITY.
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium text-zinc-500 dark:text-zinc-400 max-w-3xl mx-auto leading-relaxed px-2 sm:px-0">
            복잡함은 걷어내고,
            <br className="hidden sm:block" />
            검토의{" "}
            <span className="text-zinc-900 dark:text-zinc-100 font-bold border-b-2 border-zinc-900 dark:border-zinc-100">
              기준
            </span>
            을 세웁니다.
          </p>
        </div>

        <div className="absolute bottom-8 sm:bottom-10 animate-bounce text-zinc-300 dark:text-zinc-600">
          <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
      </section>

      {/* 2. Problem & Solution */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 lg:px-12 bg-zinc-900 dark:bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left: 문제 제기 */}
            <div
              ref={(el) => {
                fadeUpRefs.current[1] = el;
              }}
              className="fade-up"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-6 sm:mb-8 leading-tight">
                경매 검토 과정은
                <br />
                <span className="text-zinc-500 dark:text-zinc-400">소모적이었습니다.</span>
              </h2>
              <p className="text-zinc-400 dark:text-zinc-500 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                흩어진 법원 공시자료, 복잡한 등기부,
                <br className="hidden sm:block" />
                매번 엑셀을 켜야 하는 단순 반복 작업들.
                <br className="hidden sm:block" />
                <br className="hidden sm:block" />
                준비 과정에 지쳐서,
                <br className="hidden sm:block" />
                정작 중요한{" "}
                <strong className="text-white dark:text-zinc-100">&apos;실질적 검토&apos;</strong>
                를 놓치고 있진 않나요?
              </p>
            </div>

            {/* Right: 해결책 (UI Mockup) */}
            <div
              ref={(el) => {
                fadeUpRefs.current[2] = el;
              }}
              className="fade-up relative"
            >
              <div className="absolute top-0 right-0 w-full h-full bg-zinc-800 dark:bg-zinc-900 rounded-2xl sm:rounded-3xl transform rotate-6 opacity-30 dark:opacity-20 scale-95 blur-sm"></div>
              <div className="relative bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 p-5 sm:p-6 md:p-8 lg:p-12 rounded-2xl sm:rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800">
                <div className="flex justify-between items-center mb-6 sm:mb-8">
                  <div className="font-black text-xl sm:text-2xl tracking-tighter text-zinc-900 dark:text-zinc-100">
                    BIDIX
                  </div>
                  <div className="bg-zinc-900 dark:bg-zinc-800 text-white dark:text-zinc-100 text-[9px] sm:text-[10px] font-bold px-2 py-1 rounded">
                    TOOLKIT
                  </div>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  {/* Step 1 */}
                  <div className="group flex items-center gap-3 sm:gap-4 p-4 sm:p-5 bg-white dark:bg-zinc-800 rounded-xl shadow-sm border-l-4 border-zinc-900 dark:border-zinc-100 hover:bg-zinc-900 dark:hover:bg-zinc-100 hover:text-white dark:hover:text-zinc-900 transition-all duration-300 cursor-pointer touch-manipulation">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-zinc-900 dark:bg-zinc-100 rounded-full flex items-center justify-center text-white dark:text-zinc-900 flex-shrink-0 group-hover:bg-white dark:group-hover:bg-zinc-900 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-all duration-300">
                      <Search className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div>
                      <p className="text-[9px] sm:text-[10px] text-zinc-400 dark:text-zinc-500 font-bold uppercase tracking-wider group-hover:text-zinc-400 dark:group-hover:text-zinc-500 transition-colors duration-300">
                        Step 1
                      </p>
                      <p className="font-bold text-base sm:text-lg group-hover:text-white dark:group-hover:text-zinc-900 transition-colors duration-300">
                        정보 자동 수집 및 정리
                      </p>
                    </div>
                  </div>
                  {/* Step 2 */}
                  <div className="group flex items-center gap-3 sm:gap-4 p-4 sm:p-5 bg-white dark:bg-zinc-800 rounded-xl shadow-sm border-l-4 border-zinc-900 dark:border-zinc-100 hover:bg-zinc-900 dark:hover:bg-zinc-100 hover:text-white dark:hover:text-zinc-900 transition-all duration-300 cursor-pointer touch-manipulation">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-zinc-900 dark:bg-zinc-100 rounded-full flex items-center justify-center text-white dark:text-zinc-900 flex-shrink-0 group-hover:bg-white dark:group-hover:bg-zinc-900 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-all duration-300">
                      <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div>
                      <p className="text-[9px] sm:text-[10px] text-zinc-400 dark:text-zinc-500 font-bold uppercase tracking-wider group-hover:text-zinc-400 dark:group-hover:text-zinc-500 transition-colors duration-300">
                        Step 2
                      </p>
                      <p className="font-bold text-base sm:text-lg group-hover:text-white dark:group-hover:text-zinc-900 transition-colors duration-300">핵심 항목 체크리스트</p>
                    </div>
                  </div>
                  {/* Step 3 */}
                  <div className="group flex items-center gap-3 sm:gap-4 p-4 sm:p-5 bg-white dark:bg-zinc-800 rounded-xl shadow-sm border-l-4 border-zinc-900 dark:border-zinc-100 hover:bg-zinc-900 dark:hover:bg-zinc-100 hover:text-white dark:hover:text-zinc-900 transition-all duration-300 cursor-pointer touch-manipulation">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-zinc-900 dark:bg-zinc-100 rounded-full flex items-center justify-center text-white dark:text-zinc-900 flex-shrink-0 group-hover:bg-white dark:group-hover:bg-zinc-900 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-all duration-300">
                      <Calculator className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div>
                      <p className="text-[9px] sm:text-[10px] text-zinc-400 dark:text-zinc-500 font-bold uppercase tracking-wider group-hover:text-zinc-400 dark:group-hover:text-zinc-500 transition-colors duration-300">
                        Step 3
                      </p>
                      <p className="font-bold text-base sm:text-lg group-hover:text-white dark:group-hover:text-zinc-900 transition-colors duration-300">수익 시뮬레이션 계산</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Philosophy - Bento Grid */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 lg:px-12 bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div
            ref={(el) => {
              fadeUpRefs.current[3] = el;
            }}
            className="mb-12 sm:mb-16 md:mb-20 fade-up"
          >
            <span className="text-zinc-400 dark:text-zinc-500 font-bold tracking-[0.2em] sm:tracking-[0.3em] text-[10px] sm:text-xs uppercase block mb-3 sm:mb-4">
              Our Principles
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-zinc-900 dark:text-zinc-100 leading-tight">
              우리는 &apos;도구&apos;의
              <br />
              본질을 지킵니다.
            </h2>
          </div>

          {/* Bento Grid Layout */}
          <div
            ref={(el) => {
              fadeUpRefs.current[4] = el;
            }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 fade-up"
          >
            {/* Card 1: Neutrality (Large) */}
            <div className="md:col-span-2 bg-zinc-50 dark:bg-zinc-900 border-2 border-zinc-900 dark:border-zinc-100 rounded-[1.5rem] sm:rounded-[2rem] p-6 sm:p-8 md:p-10 relative overflow-hidden group hover:bg-zinc-900 dark:hover:bg-zinc-50 hover:text-white dark:hover:text-zinc-900 transition-colors duration-500">
              <div className="relative z-10 flex flex-col h-full justify-between min-h-[200px] sm:min-h-[240px]">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white dark:bg-zinc-800 border-2 border-zinc-900 dark:border-zinc-100 rounded-full flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-zinc-800 dark:group-hover:bg-zinc-900 group-hover:border-zinc-700 dark:group-hover:border-zinc-100 transition-colors">
                  <Scale className="w-6 h-6 sm:w-7 sm:h-7 text-zinc-900 dark:text-zinc-100 group-hover:text-white dark:group-hover:text-zinc-900 transition-colors" />
                </div>
                <div>
                  <h3 className="text-2xl sm:text-3xl font-black mb-3 sm:mb-4">
                    중립성 (Neutrality)
                  </h3>
                  <p className="text-zinc-500 dark:text-zinc-400 text-base sm:text-lg leading-relaxed group-hover:text-zinc-400 dark:group-hover:text-zinc-500 max-w-lg">
                    우리는 &quot;좋다&quot;, &quot;나쁘다&quot;를 평가하지
                    않습니다.
                    <br className="hidden sm:block" />
                    입력된 조건에 따른{" "}
                    <strong className="text-zinc-900 dark:text-zinc-100 group-hover:text-white dark:group-hover:text-zinc-900">
                      객관적인 결과값
                    </strong>
                    만을
                    <br className="hidden sm:block" />
                    있는 그대로 보여줍니다.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2: User-Driven (Tall) */}
            <div className="group bg-zinc-900 dark:bg-zinc-800 text-white dark:text-zinc-100 border-2 border-zinc-900 dark:border-zinc-100 rounded-[1.5rem] sm:rounded-[2rem] p-6 sm:p-8 md:p-10 relative overflow-hidden hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors duration-500 cursor-pointer touch-manipulation">
              <div className="relative z-10 flex flex-col h-full justify-between min-h-[200px] sm:min-h-[240px]">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-zinc-800 dark:bg-zinc-700 rounded-full flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-zinc-900 dark:group-hover:bg-zinc-800 group-hover:border-2 group-hover:border-zinc-900 dark:group-hover:border-zinc-100 transition-colors">
                  <Sliders className="w-6 h-6 sm:w-7 sm:h-7 text-white dark:text-zinc-100 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-black mb-3 sm:mb-4">
                    사용자 기준
                    <br />
                    <span className="text-zinc-500 dark:text-zinc-400 text-lg sm:text-xl group-hover:text-zinc-400 dark:group-hover:text-zinc-500 transition-colors">(User-Driven)</span>
                  </h3>
                  <p className="text-zinc-400 dark:text-zinc-500 text-sm sm:text-base leading-relaxed group-hover:text-zinc-500 dark:group-hover:text-zinc-400 transition-colors">
                    정해진 값을 강요하지 않고,
                    <br className="hidden sm:block" />
                    <strong className="text-white dark:text-zinc-100 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors">사용자의 입력값(Input value)</strong>
                    을
                    <br className="hidden sm:block" />
                    존중하여 계산합니다.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 3: Archive (Wide) */}
            <div className="group md:col-span-3 border border-zinc-200 dark:border-zinc-800 rounded-[1.5rem] sm:rounded-[2rem] p-6 sm:p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 sm:gap-10 hover:bg-zinc-900 dark:hover:bg-zinc-800 hover:text-white dark:hover:text-zinc-100 hover:border-zinc-900 dark:hover:border-zinc-700 hover:shadow-2xl transition-all duration-500 cursor-pointer touch-manipulation">
              <div className="flex-1 w-full md:w-auto">
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center group-hover:bg-zinc-800 dark:group-hover:bg-zinc-700 group-hover:border-2 group-hover:border-zinc-700 dark:group-hover:border-zinc-600 transition-all">
                    <Save className="w-6 h-6 sm:w-7 sm:h-7 text-zinc-900 dark:text-zinc-100 group-hover:text-white dark:group-hover:text-zinc-100 transition-colors" />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black group-hover:text-white dark:group-hover:text-zinc-100 transition-colors">기록 (Archive)</h3>
                </div>
                <p className="text-zinc-500 dark:text-zinc-400 text-base sm:text-lg leading-relaxed group-hover:text-zinc-400 dark:group-hover:text-zinc-300 transition-colors">
                  그때 왜 그 숫자를 입력했는지 기록합니다.
                  <br className="hidden sm:block" />
                  성공과 실패의 원인을 복기할 수 있는{" "}
                  <strong className="text-zinc-900 dark:text-zinc-100 group-hover:text-white dark:group-hover:text-zinc-100 transition-colors">소중한 데이터 자산</strong>
                  으로 남겨드립니다.
                </p>
              </div>
              {/* Visual Deco */}
              <div className="flex gap-2 opacity-50 grayscale group-hover:opacity-70 group-hover:grayscale-0 transition-all duration-500">
                <div className="w-24 h-16 sm:w-32 sm:h-20 bg-zinc-100 dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 group-hover:bg-zinc-800 dark:group-hover:bg-zinc-700 group-hover:border-zinc-700 dark:group-hover:border-zinc-600 transition-colors"></div>
                <div className="w-24 h-16 sm:w-32 sm:h-20 bg-zinc-200 dark:bg-zinc-700 rounded-lg border border-zinc-300 dark:border-zinc-600 group-hover:bg-zinc-700 dark:group-hover:bg-zinc-600 group-hover:border-zinc-600 dark:group-hover:border-zinc-500 transition-colors"></div>
                <div className="w-24 h-16 sm:w-32 sm:h-20 bg-zinc-800 dark:bg-zinc-600 rounded-lg group-hover:bg-zinc-600 dark:group-hover:bg-zinc-500 transition-colors"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Origin Story */}
      <section className="py-20 sm:py-32 md:py-40 px-4 sm:px-6 lg:px-12 max-w-screen-md mx-auto text-center bg-white dark:bg-zinc-950">
        <div
          ref={(el) => {
            fadeUpRefs.current[5] = el;
          }}
          className="fade-up"
        >
          <Quote className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-zinc-200 dark:text-zinc-800 mb-6 sm:mb-8 mx-auto" />
          <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black leading-snug mb-8 sm:mb-10 md:mb-12 text-zinc-900 dark:text-zinc-100 tracking-tight px-2 sm:px-0">
            &quot;BIDIX는 책이나 이론이 아니라,
            <br className="hidden sm:block" />
            <span className="text-zinc-400 dark:text-zinc-500 decoration-4 underline decoration-zinc-200 dark:decoration-zinc-800 underline-offset-6 sm:underline-offset-8">
              실제 현장의 반복된 불편함
            </span>
            에서
            <br className="hidden sm:block" />
            출발했습니다.&quot;
          </h3>

          <p className="text-zinc-500 dark:text-zinc-400 leading-relaxed text-base sm:text-lg md:text-xl font-medium px-2 sm:px-0">
            거창한 인공지능도, 미래 예측 모델도 아닙니다.
            <br className="hidden sm:block" />
            그저 사용자가 덜 귀찮게, 더 깊이 있게 검토할 수 있도록 돕는
            <br className="hidden sm:block" />
            <strong className="text-zinc-900 dark:text-zinc-100">
              &apos;가장 실용적인 계산기&apos;
            </strong>
            를 만드는 데 집중했습니다.
          </p>
        </div>
      </section>

      {/* 5. Footer - CTA */}
      <footer className="py-16 sm:py-24 md:py-32 bg-zinc-900 dark:bg-zinc-950 text-white text-center px-4 sm:px-6">
        <div
          ref={(el) => {
            fadeUpRefs.current[6] = el;
          }}
          className="fade-up"
        >
          <p className="text-zinc-500 dark:text-zinc-400 text-[10px] sm:text-xs font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-4 sm:mb-6">
            Our Vision
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mb-8 sm:mb-10">
            Check Easy,
            <br />
            Think Better.
          </h2>
          <div className="flex justify-center">
            <Button
              asChild
              className="px-8 sm:px-10 py-3 sm:py-4 bg-white dark:bg-zinc-100 text-zinc-900 dark:text-zinc-900 font-black rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-200 transition transform hover:-translate-y-1 min-h-[48px] touch-manipulation text-base sm:text-lg"
            >
              <Link
                href="/"
                className="flex items-center gap-2"
                onClick={() => {
                  trackEvent(
                    "brand_story_cta_click",
                    {
                      source: "brand_story_page",
                      button_location: "footer_cta",
                      redirect_to: "landing_page",
                      user_type: userId ? "authenticated" : "anonymous",
                    },
                    "navigation",
                    { includeTrafficSource: true },
                  );
                }}
              >
                서비스 시작하기
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </footer>

      <style jsx global>{`
        .fade-up {
          opacity: 0;
          transform: translateY(30px);
          transition: all 1s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .fade-up.visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}
